// src/generators/dzial2_algebra/temat5_zadania_uzupelniajace_maturalne/
// podtemat3_maturalne_otwarte_types/MaturaPercentLinearSystemOpenTask.js

import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import FractionInput, {
  formatLatexPreview as formatFracPreview,
} from "../../../../components/inputs/FractionInput";

import {
  parseRootFractionInput,
  formatLatexPreview as formatNumLatex,
} from "../../../../components/inputs/RootFractionInput";

import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";

/* ============================================================================
 * HELPERY
 * ========================================================================== */

const tol = 1e-6;
const eq = (a, b) => Math.abs(a - b) <= tol;

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a || 1;
}

function normalizeUserNumberText(txt) {
  return String(txt ?? "")
    .replace(/[−–—]/g, "-")
    .replace(/\u00A0|\u202F|\u2009|\u200A|\u200B/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parseNumberFromAny(v) {
  if (v == null) return NaN;

  if (typeof v === "object") {
    if (Number.isFinite(v.parsed)) return v.parsed;
    if (typeof v.raw === "string") {
      const s = normalizeUserNumberText(v.raw).replace(",", ".");
      return parseRootFractionInput(s);
    }
  }

  if (typeof v === "number") return v;

  if (typeof v === "string") {
    const s = normalizeUserNumberText(v).replace(",", ".");
    return parseRootFractionInput(s);
  }

  return NaN;
}

function getFractionRaw(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && typeof v.raw === "string") return v.raw ?? "";
  return "";
}

/** Podgląd LaTeX dla równania wpisanego w EquationInput */
function toLatexEquationPreview(str) {
  let s = String(str ?? "");

  // mieszane ułamki: "3 1/2" -> "3\\dfrac{1}{2}"
  s = s.replace(
    /(-?\d+)\s+(\d+)\/(\d+)/g,
    (_, w, n, d) => `${w}\\dfrac{${n}}{${d}}`
  );

  // zwykłe ułamki: "-3/4" -> "\\dfrac{-3}{4}"
  s = s.replace(
    /(?<!\\dfrac\{)(-?\d+)\/(\d+)/g,
    (_, n, d) => `\\dfrac{${n}}{${d}}`
  );

  // wielocyfrowe wykładniki: 2^36 -> 2^{36}, 2^-12 -> 2^{-12}
  s = s.replace(/\^(-?\d{2,})/g, "^{$1}");

  // mnożenie: gwiazdka lub środkowa kropka -> \\cdot
  s = s.replace(/\*/g, "\\cdot ");
  s = s.replace(/·/g, "\\cdot ");

  return s;
}

/* ============================================================================
 * PARSOWANIE RÓWNAŃ LINIOWYCH
 * ========================================================================== */

function parseLinearExpr(expr) {
  if (expr == null) return null;
  let s = String(expr).trim();
  if (!s) return { ax: 0, ay: 0, c: 0 };

  s = s.replace(/,/g, ".");    // przecinki jako kropki
  s = s.replace(/\s+/g, "");   // usuwamy spacje
  s = s.replace(/·/g, "*");    // środkowa kropka -> zwykła gwiazdka

  // zamiana na sumę: "-" -> "+-"
  s = s.replace(/-/g, "+-");
  if (s[0] === "+") s = s.slice(1);

  const parts = s.split("+").filter((t) => t !== "");
  if (!parts.length) return null;

  let ax = 0;
  let ay = 0;
  let c = 0;

  // pomocniczo: parsujemy iloczyn typu "0.7*0.85*2"
  const parseProduct = (str) => {
    if (str === "" || str === "+") return 1;
    if (str === "-") return -1;

    const factors = str.split("*").filter((x) => x !== "");
    let val = 1;

    const parseFactor = (token) => {
      if (token === "" || token === "+") return 1;
      if (token === "-") return -1;

      // ułamek zwykły: a/b (a i b mogą być całkowite lub dziesiętne, mogą mieć znak)
      // np. "4/5", "-17/20", "0.9/10"
      if (token.includes("/")) {
        const parts = token.split("/").filter((p) => p !== "");
        if (parts.length !== 2) return NaN;
        const a = parseFloat(parts[0]);
        const b = parseFloat(parts[1]);
        if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return NaN;
        return a / b;
      }

      const num = parseFloat(token);
      if (!Number.isFinite(num)) return NaN;
      return num;
    };

    for (const f of factors) {
      const num = parseFactor(f);
      if (!Number.isFinite(num)) return NaN;
      val *= num;
    }
    return val;
  };

  for (const term of parts) {
    let t = term;

    // nie obsługujemy xy, x^2 itp.
    if (t.includes("x") && t.includes("y")) {
      return null;
    }

    if (t.includes("x")) {
      const coef = parseProduct(t.replace("x", ""));
      if (!Number.isFinite(coef)) return null;
      ax += coef;
      continue;
    }

    if (t.includes("y")) {
      const coef = parseProduct(t.replace("y", ""));
      if (!Number.isFinite(coef)) return null;
      ay += coef;
      continue;
    }

    // wyraz wolny – też może być iloczynem, np. "-2*3"
    const val = parseProduct(t);
    if (!Number.isFinite(val)) return null;
    c += val;
  }

  return { ax, ay, c };
}


/**
 * Zamienia równanie "lewa = prawa" na postać ax*x + ay*y + c = 0
 */
function parseEquationToStandard(str) {
  if (str == null) return null;
  let s = String(str).trim();
  if (!s) return null;

  s = s.replace(/,/g, ".");
  s = s.replace(/\s+/g, "");

  const parts = s.split("=");
  if (parts.length !== 2) return null;

  const left = parseLinearExpr(parts[0]);
  const right = parseLinearExpr(parts[1]);
  if (!left || !right) return null;

  return {
    ax: left.ax - right.ax,
    ay: left.ay - right.ay,
    c: left.c - right.c,
  };
}

/** Sprawdza równoważność dwóch równań (do stałej k != 0) */
function equationsEquivalent(e1, e2) {
  if (!e1 || !e2) return false;

  const { ax: a1, ay: b1, c: c1 } = e1;
  const { ax: a2, ay: b2, c: c2 } = e2;

  // jeżeli wszystkie współczynniki jednego są 0 -> odrzucamy
  if (eq(a1, 0) && eq(b1, 0) && eq(c1, 0)) return false;
  if (eq(a2, 0) && eq(b2, 0) && eq(c2, 0)) return false;

  // wybieramy bazowy współczynnik z równania wzorcowego
  let baseNum;
  let baseDen;
  if (!eq(a2, 0)) {
    baseNum = a1;
    baseDen = a2;
  } else if (!eq(b2, 0)) {
    baseNum = b1;
    baseDen = b2;
  } else {
    baseNum = c1;
    baseDen = c2;
  }

  if (eq(baseDen, 0)) return false;

  const k = baseNum / baseDen;
  if (!Number.isFinite(k) || eq(k, 0)) return false;

  return (
    eq(a1, k * a2) &&
    eq(b1, k * b2) &&
    eq(c1, k * c2)
  );
}

function decimalLatex(value, digits = 2) {
  // zawsze kropka jako separator
  return value.toFixed(digits);
}

function isBareSolvedEquation(value) {
  const normalized = String(value ?? "").replace(/\s+/g, "").replace(/,/g, ".");
  const numberPattern = "[+-]?(?:\\d+(?:\\.\\d+)?|\\d+\\/\\d+)";
  return new RegExp(`^(?:[xy]=${numberPattern}|${numberPattern}=[xy])$`).test(normalized);
}

function integerLatex(value) {
  return String(Math.trunc(value)).replace(/\B(?=(\d{3})+(?!\d))/g, "\\,");
}

function isBusinessScenario(scenario) {
  return scenario?.id === "marketing" || scenario?.id === "researchBudget";
}

function businessText(text, enabled) {
  if (!enabled) return text;
  return String(text).split(/\b([ABXY])\b/).map((part, index) =>
    /^[ABXY]$/.test(part) ? <InlineMath key={index} math={part} /> : part
  );
}

function normalizeBusinessVars(expr) {
  if (expr == null) return expr;
  return String(expr).replace(/[aA]/g, "x").replace(/[bB]/g, "y");
}

/* ============================================================================
 * GENERATOR FABUŁ
 * ========================================================================== */

function pickScenario(forcedId = null) {
  const scenarios = [
    {
      id: "orchards",
      title: "Zadanie o sadach",
      objectTotal: "drzew",
      item1: "w pierwszym sadzie",
      item2: "w drugim sadzie",
      contextIntro:
        "W pewnym roku założono dwa sady. Posadzono w nich łącznie",
      diedVerb: "uschło",
      remainedVerb: "pozostały",
      relationPhrase:
        "Liczba drzew, które pozostały w drugim sadzie, stanowiła",
      unitObjects: "drzew w tym sadzie",
      questionPrefix: "Oblicz, ile drzew posadzono",
      questionObject: "w pierwszym sadzie.",
      answerNoun: "drzew",
    },
    {
      id: "clothes",
      title: "Kontrola jakości ubrań",
      objectTotal: "sztuk ubrań",
      item1: "koszulek",
      item2: "spodni",
      contextIntro:
        "W zakładzie odzieżowym wyprodukowano pewną partię koszulek i spodni. Łącznie uszyto",
      diedVerb: "nie przeszło kontroli jakości",
      remainedVerb: "przeszły kontrolę jakości",
      relationPhrase:
        "Liczba spodni, które przeszły kontrolę jakości, stanowiła",
      unitObjects: "ubrania z tej partii",
      questionPrefix: "Oblicz, ile koszulek uszyto w tej partii",
      questionObject: ".",
      answerNoun: "koszulek",
    },
    {
      id: "fruits",
      title: "Sortownia owoców",
      objectTotal: "skrzynki owoców",
      item1: "jabłek",
      item2: "gruszek",
      contextIntro:
        "Do sortowni owoców przywieziono skrzynki z jabłkami i gruszkami. Łącznie było",
      diedVerb: "zostało odrzuconych podczas sortowania",
      remainedVerb: "zostały zakwalifikowane jako towar handlowy",
      relationPhrase:
        "Liczba skrzynek gruszek zakwalifikowanych jako towar handlowy stanowiła",
      unitObjects: "skrzynki owoców",
      questionPrefix:
        "Oblicz, ile skrzynek z jabłkami przywieziono do sortowni",
      questionObject: ".",
      answerNoun: "skrzynki z jabłkami",
    },
    {
      id: "contest",
      title: "Konkurs szkolny",
      objectTotal: "uczestników",
      item1: "chłopców",
      item2: "dziewczynek",
      contextIntro:
        "Do szkolnego konkursu matematycznego zgłosiło się łącznie",
      diedVerb: "odpadło w eliminacjach",
      remainedVerb: "zakwalifikowało się do finału",
      relationPhrase:
        "Liczba dziewczynek zakwalifikowanych do finału stanowiła",
      unitObjects: "uczestników konkursu",
      questionPrefix:
        "Oblicz, ilu chłopców zgłosiło się do konkursu na początku",
      questionObject: ".",
      answerNoun: "chłopców",
    },
    {
      id: "researchBudget",
      title: "Budżet na projekty badawcze",
      objectTotal: "zł",
      item1: "zespołowi X",
      item2: "zespołowi Y",
      contextIntro:
        "Zarząd firmy wydzielił z budżetu kwotę łącznie na projekty badawcze dla dwóch zespołów. Łącznie przydzielono",
      questionPrefix: "Oblicz, jaką kwotę przydzielono",
      questionObject: "zespołowi X.",
      answerNoun: "zł",
    },
    {
      id: "marketing",
      title: "Budżet marketingowy",
      objectTotal: "zł",
      item1: "kampanii X",
      item2: "kampanii Y",
      contextIntro:
        "Dział marketingu podzielił budżet reklamowy między dwie kampanie. Łącznie przydzielono",
      questionPrefix: "Oblicz, jaką kwotę przydzielono",
      questionObject: "kampanii X.",
      answerNoun: "zł",
    },
    {
      id: "it",
      title: "Projekt IT",
      objectTotal: "zadań",
      item1: "w module A",
      item2: "w module B",
      contextIntro:
        "W projekcie IT zaplanowano zadania dla dwóch modułów. Łącznie zaplanowano",
      questionPrefix: "Oblicz, ile zadań zaplanowano",
      questionObject: "w module A.",
      answerNoun: "zadań",
    },
  ];

  if (forcedId) {
    const forced = scenarios.find((s) => s.id === forcedId);
    if (forced) return forced;
  }

  // "Stare" zadanie losuje tylko klasyczne warianty fabularne.
  const legacyIds = ["orchards", "clothes", "fruits", "contest"];
  const legacyScenarios = scenarios.filter((s) => legacyIds.includes(s.id));
  const idx = randInt(0, legacyScenarios.length - 1);
  return legacyScenarios[idx];
}

/* ============================================================================
 * GENERATOR ZADANIA
 * ========================================================================== */

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_CZE") {
    return {
      type: "MaturaPercentLinearSystemOpenTask",
      data: {
        scenario: pickScenario("orchards"),
        totalInitial: 1410,
        p1: 20,
        p2: 15,
        r: 70,
        a1: 0.8,
        a2: 0.85,
        kRatio: 0.7,
        xCorrect: 850,
        yCorrect: 560,
        usedTotal: 254,
        maxPoints: 2,
        originalContext: {
          opening:
            "W maju 2024 roku założono dwa sady. Posadzono w nich łącznie",
          timing: "Po upływie roku",
        },
      },
      source: {
        variant: "original",
        exam: "MATURA",
        session: "2025_CZE",
        taskNumber: 8,
      },
    };
  }

  const scenario = pickScenario(options.scenarioId ?? null);
  const minTotal = Number.isFinite(options.minTotal) ? options.minTotal : 400;
  const maxTotal = Number.isFinite(options.maxTotal) ? options.maxTotal : 2000;
  const scaleFactor = Number.isFinite(options.scaleFactor) ? options.scaleFactor : 1;

  // zakresy procentów (w %)
  let p1Options, p2Options, rOptions;

  switch (scenario.id) {
    case "researchBudget":
      p1Options = [10, 12, 15, 20, 25];
      p2Options = [5, 8, 10, 12, 15, 20];
      rOptions = [50, 60, 70, 80, 90];
      break;
    case "marketing":
      p1Options = [10, 12, 15, 20, 25];
      p2Options = [5, 8, 10, 12, 15, 20];
      rOptions = [50, 60, 70, 80, 90];
      break;
    case "it":
      p1Options = [15, 20, 25, 30, 35];
      p2Options = [10, 15, 20, 25, 30];
      rOptions = [40, 50, 60, 70, 80];
      break;
    case "clothes":
      p1Options = [10, 15, 20, 25, 30]; // koszulki odrzucone
      p2Options = [5, 10, 15, 20]; // spodnie odrzucone
      rOptions = [40, 50, 60, 70, 80, 90];
      break;
    case "fruits":
      p1Options = [5, 10, 15]; // jabłka odrzucone
      p2Options = [5, 10, 15, 20, 25]; // gruszki odrzucone
      rOptions = [50, 60, 70, 80];
      break;
    case "contest":
      p1Options = [10, 20, 30, 40]; // chłopcy odpadli
      p2Options = [10, 20, 30, 40]; // dziewczynki odpadły
      rOptions = [50, 60, 70, 80, 90];
      break;
    case "orchards":
    default:
      p1Options = [10, 15, 20, 25, 30]; // pierwszy sad uschnięte
      p2Options = [10, 15, 20, 25]; // drugi sad uschnięte
      rOptions = [50, 60, 70, 80];
      break;
  }

  let p1, p2, r;
  let xCorrect, yCorrect, totalInitial;
  let a1, a2, kRatio;

  // próbujemy wylosować sensowny zestaw parametrów
  for (;;) {
    p1 = p1Options[randInt(0, p1Options.length - 1)];
    p2 = p2Options[randInt(0, p2Options.length - 1)];

        // unikamy zduplikowanych procentów w obu grupach
    if (p1 === p2) continue;

    r = rOptions[randInt(0, rOptions.length - 1)];

    a1 = (100 - p1) / 100; // część, która zostaje w 1.
    a2 = (100 - p2) / 100; // część, która zostaje w 2.
    kRatio = r / 100; // np. 0.7

    // y = (kRatio * a1 / a2) * x
    let num = r * (100 - p1); // licznik (bez /100)
    let den = (100 - p2) * 100; // mianownik

    const g = gcd(num, den);
    num /= g;
    den /= g;

    // wybieramy k tak, aby łączna liczba była w rozsądnym zakresie
    const baseSum = num + den; // T = k * baseSum
    const kMin = Math.ceil(minTotal / baseSum);
    const kMax = Math.floor(maxTotal / baseSum);

    if (kMin > kMax) continue;

    const k = randInt(Math.max(1, kMin), Math.max(kMin, kMax));

    const xRaw = den * k;
    const yRaw = num * k;
    const totalRaw = xRaw + yRaw;

    if (totalRaw < minTotal || totalRaw > maxTotal) continue;

    xCorrect = xRaw * scaleFactor;
    yCorrect = yRaw * scaleFactor;
    totalInitial = totalRaw * scaleFactor;
    break;
  }

  const usedTotal = (p1 / 100) * xCorrect + (p2 / 100) * yCorrect;

  return {
    type: "MaturaPercentLinearSystemOpenTask",
    data: {
      scenario,
      totalInitial,
      p1,
      p2,
      r,
      a1,
      a2,
      kRatio,
      xCorrect,
      yCorrect,
      usedTotal,
      maxPoints: 2,
    },
    source: { variant: "random", basedOn: "MATURA_2025_CZE_8" },
  };
}

/* ============================================================================
 * TREŚĆ ZADANIA
 * ========================================================================== */

export function renderTask({ data }) {
  const {
    scenario,
    totalInitial,
    p1,
    p2,
    r,
    usedTotal,
    originalContext,
  } = data;
  const isBusiness = isBusinessScenario(scenario);

  const totalLatex = integerLatex(totalInitial);
  const usedLatex = integerLatex(usedTotal);
  const p1Latex = `${p1}\\%`;
  const p2Latex = `${p2}\\%`;
  const rLatex = `${r}\\%`;

  const cat1 = businessText(scenario.item1, isBusiness);
  const cat2 = businessText(scenario.item2, isBusiness);

  let xMeaning = "";
  let yMeaning = "";
  let rejectionSentence = "";
  let relationSentence = "";

  /* =======================
     FRUITS – SORTOWNIA
     ======================= */
  if (scenario.id === "fruits") {
    xMeaning = `liczbę skrzynek ${cat1}`;
    yMeaning = `liczbę skrzynek ${cat2}`;

    rejectionSentence = (
      <>
        Po sortowaniu odrzucono <InlineMath math={p1Latex} /> skrzynek {cat1} oraz{" "}
        <InlineMath math={p2Latex} /> skrzynek {cat2}. Odrzucone skrzynki usunięto.
      </>
    );

    relationSentence = (
      <>
        Liczba skrzynek {cat2} zakwalifikowanych jako towar handlowy stanowiła{" "}
        <InlineMath math={rLatex} /> liczby skrzynek {cat1}, które zostały
        zakwalifikowane jako towar handlowy.
      </>
    );
  }

  /* =======================
     CONTEST – KONKURS
     ======================= */
  else if (scenario.id === "contest") {
    xMeaning = `liczbę ${cat1}`;
    yMeaning = `liczbę ${cat2}`;

    rejectionSentence = (
      <>
        W eliminacjach odpadło <InlineMath math={p1Latex} /> {cat1} oraz{" "}
        <InlineMath math={p2Latex} /> {cat2}.
      </>
    );

    relationSentence = (
      <>
        Liczba {cat2}, które zakwalifikowały się do finału, stanowiła{" "}
        <InlineMath math={rLatex} /> liczby {cat1}, którzy zakwalifikowali się do
        finału.
      </>
    );
  }

  /* =======================
     MARKETING – BUDŻET
     ======================= */
  else if (scenario.id === "marketing") {
    xMeaning = <>kwotę przydzieloną {cat1}</>;
    yMeaning = <>kwotę przydzieloną {cat2}</>;

    rejectionSentence = (
      <>
        W pierwszym okresie wykorzystano łącznie{" "}
        <InlineMath math={usedLatex} /> zł: <InlineMath math={p1Latex} />{" "}
        środków przydzielonych {cat1} oraz <InlineMath math={p2Latex} />{" "}
        środków przydzielonych {cat2}.
      </>
    );

    // Wariant CKE: tu wystarczą 2 informacje (suma przydzielona + suma wykorzystana procentowo).
    relationSentence = "";
  }
  else if (scenario.id === "researchBudget") {
    xMeaning = <>kwotę przydzieloną {cat1}</>;
    yMeaning = <>kwotę przydzieloną {cat2}</>;

    rejectionSentence = (
      <>
        W pierwszym półroczu wykorzystano łącznie{" "}
        <InlineMath math={usedLatex} /> zł: <InlineMath math={p1Latex} />{" "}
        środków przydzielonych {cat1} oraz <InlineMath math={p2Latex} />{" "}
        środków przydzielonych {cat2}.
      </>
    );

    // Wariant CKE: tu wystarczą 2 informacje (suma przydzielona + suma wykorzystana procentowo).
    relationSentence = "";
  }

  /* =======================
     IT – MODUŁY
     ======================= */
  else if (scenario.id === "it") {
    xMeaning = `liczbę zadań ${cat1}`;
    yMeaning = `liczbę zadań ${cat2}`;

    rejectionSentence = (
      <>
        Po pierwszym sprincie zrealizowano <InlineMath math={p1Latex} /> zadań
        zaplanowanych {cat1} oraz <InlineMath math={p2Latex} /> zadań
        zaplanowanych {cat2}.
      </>
    );

    relationSentence = (
      <>
        Liczba zadań zrealizowanych {cat2} stanowiła{" "}
        <InlineMath math={rLatex} /> liczby zadań zrealizowanych {cat1}.
      </>
    );
  }

  /* =======================
     ORCHARDS – SADY
     ======================= */
  else if (scenario.id === "orchards") {
    xMeaning = `liczbę drzew ${cat1}`;
    yMeaning = `liczbę drzew ${cat2}`;

    rejectionSentence = (
      <>
        {originalContext?.timing || "Po pewnym czasie"} okazało się, że uschło{" "}
        <InlineMath math={p1Latex} /> drzew{" "}
        {cat1} oraz <InlineMath math={p2Latex} /> drzew {cat2}.
      </>
    );

    relationSentence = (
      <>
        Liczba drzew, które pozostały {cat2}, stanowiła{" "}
        <InlineMath math={rLatex} /> liczby drzew, które pozostały {cat1}.
      </>
    );
  }

  /* =======================
     CLOTHES – KONTROLA JAKOŚCI
     ======================= */
  else {
    xMeaning = `liczbę ${cat1}`;
    yMeaning = `liczbę ${cat2}`;

    rejectionSentence = (
      <>
        Podczas kontroli odrzucono <InlineMath math={p1Latex} /> {cat1} oraz{" "}
        <InlineMath math={p2Latex} /> {cat2}.
      </>
    );

    relationSentence = (
      <>
        Liczba {cat2}, które przeszły kontrolę, stanowiła{" "}
        <InlineMath math={rLatex} /> liczby {cat1}, które przeszły kontrolę.
      </>
    );
  }

  return (
    <div style={{ lineHeight: 1.5 }}>
      <p>
        {businessText(originalContext?.opening || scenario.contextIntro, isBusiness)}{" "}
        <InlineMath math={totalLatex} />{" "}
        {scenario.objectTotal}. Przyjmijmy, że <InlineMath math={"x"} /> oznacza{" "}
        {xMeaning}, a <InlineMath math={"y"} /> oznacza {yMeaning}.
      </p>

      <p style={{ marginTop: "0.4rem" }}>{rejectionSentence}</p>

      {!!relationSentence && (
        <p style={{ marginTop: "0.4rem" }}>{relationSentence}</p>
      )}

      <p style={{ marginTop: "0.5rem" }}>
        Zapisz jedno równanie z niewiadomą <InlineMath math={"x"} /> lub układ
        równań z niewiadomymi <InlineMath math={"x"} /> i{" "}
        <InlineMath math={"y"} />, opisujący tę sytuację.
      </p>

      <p style={{ marginTop: "0.25rem" }}>
        {isBusiness ? (
          <>
            {scenario.questionPrefix} {businessText(scenario.questionObject, true)}
          </>
        ) : (
          <>
            Następnie oblicz, ile wynosi <InlineMath math={"x"} />.{" "}
            {scenario.questionPrefix} {scenario.questionObject}
          </>
        )}
      </p>
    </div>
  );
}

/* ============================================================================
 * INPUT UŻYTKOWNIKA
 * ========================================================================== */

export function renderInput({ data }, userInput = {}, onChange, disabled) {
  const { scenario } = data;
  const isBusiness = isBusinessScenario(scenario);
  const { eq1 = "", eq2 = "", x = "" } = userInput;

  return (
    <div className="flex flex-col gap-4">
      <OpenTaskScoringHint variant="methodOptional" />

      {/* Krok 1 – równanie / układ równań */}
      <div className="flex flex-col gap-2">
        <strong>Krok 1.</strong>
        <div>
          Zapisz jedno równanie z niewiadomą <InlineMath math={"x"} /> lub układ
          równań z niewiadomymi <InlineMath math={"x"} /> i{" "}
          <InlineMath math={"y"} />, opisujący sytuację z zadania.
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <div>Pierwsze równanie:</div>
            <EquationInput
              value={eq1}
              onChange={(val) => onChange({ ...userInput, eq1: val })}
              disabled={disabled}
              placeholder={
                isBusiness ? "np. x + y = 1200000" : "np. x + y = 1410"
              }
            />
          </div>

          <div className="flex flex-col gap-1">
            <div>Drugie równanie (opcjonalnie):</div>
            <EquationInput
              value={eq2}
              onChange={(val) => onChange({ ...userInput, eq2: val })}
              disabled={disabled}
              placeholder={
                isBusiness
                  ? "np. 0.13x + 0.11y = 146700"
                  : "np. 0.85y = 0.70·0.8x"
              }
            />
          </div>
        </div>
      </div>

      {/* Krok 2 – rozwiązanie (x) */}
      <div className="flex flex-col gap-2">
        <strong>Krok 2.</strong>
        <div>
          Rozwiąż zadanie i oblicz, ile wynosi{" "}
          <InlineMath math={"x"} /> (
          {isBusiness ? "kwota przydzielona " : "liczba "}
          {scenario.item1}).
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <InlineMath math={"x\\;="} />
          <FractionInput
            value={x}
            onChange={(val) => onChange({ ...userInput, x: val })}
            disabled={disabled}
            label="x"
            placeholder="np. 850"
          />
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * PODGLĄD ODPOWIEDZI UŻYTKOWNIKA
 * ========================================================================== */

export function renderUserAnswer(userInput = {}, task = null) {
  void task;
  const eq1 = String(userInput.eq1 ?? "").trim();
  const eq2 = String(userInput.eq2 ?? "").trim();

  const xRaw = getFractionRaw(userInput.x);
  const xLatex =
    xRaw &&
    (formatFracPreview(xRaw, "") ||
      formatNumLatex(String(parseNumberFromAny(userInput.x)), ""));

  const anyEq = eq1 || eq2;

  if (!anyEq && !xLatex) return <i>(brak odpowiedzi)</i>;

  return (
    <div className="space-y-1" style={{ textAlign: "left" }}>
      {anyEq && (
        <div>
          <BlockMath
            math={String.raw`\begin{cases}
${toLatexEquationPreview(eq1 || "")} \\
${toLatexEquationPreview(eq2 || "")}
\end{cases}`}
          />
        </div>
      )}
      {xLatex && (
        <div>
          <InlineMath math={`x = ${xLatex}`} />
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * KRÓTKA POPRAWNA ODPOWIEDŹ
 * ========================================================================== */

export function renderAnswer({ data }) {
  const { scenario, totalInitial, p1, p2, r, xCorrect } = data;

  const a1 = (100 - p1) / 100;
  const a2 = (100 - p2) / 100;
  const kRatio = r / 100;
  const isBusiness =
    scenario?.id === "marketing" || scenario?.id === "researchBudget";

  const totalLatex = integerLatex(totalInitial);
  const usedLatex = integerLatex(data.usedTotal);
  const p1UsedLatex = decimalLatex(p1 / 100, 2);
  const p2UsedLatex = decimalLatex(p2 / 100, 2);
  const a1Latex = decimalLatex(a1, 2);
  const a2Latex = decimalLatex(a2, 2);
  const kLatex = decimalLatex(kRatio, 2);

  const defaultSystemLatex = String.raw`\begin{cases}
x + y = ${totalLatex} \\
${a2Latex}y = ${kLatex}\cdot ${a1Latex}x
\end{cases}`;
  const businessSystemLatex = String.raw`\begin{cases}
x + y = ${totalLatex} \\
${p1UsedLatex}x + ${p2UsedLatex}y = ${usedLatex}
\end{cases}`;
  const systemLatex = isBusiness ? businessSystemLatex : defaultSystemLatex;

  const solLatex = String.raw`x = ${integerLatex(xCorrect)}${isBusiness ? String.raw`\;\text{zł}` : ""}`;

  return (
    <div
      style={{
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: "0.4rem",
        lineHeight: 1.4,
      }}
    >
      <div>
        <BlockMath math={systemLatex} />
      </div>
      <div>
        <InlineMath math={solLatex} />
      </div>
    </div>
  );
}

/* ============================================================================
 * SPRAWDZANIE (0–2 pkt)
 * ========================================================================== */

export function checkAnswer({ data }, userInput = {}) {
  const { scenario, totalInitial, p1, p2, r, xCorrect, maxPoints, usedTotal } = data;

  const a1 = (100 - p1) / 100;
  const a2 = (100 - p2) / 100;
  const kRatio = r / 100;
  const isBusiness =
    scenario?.id === "marketing" || scenario?.id === "researchBudget";

  const eq1Str = isBusiness ? normalizeBusinessVars(userInput.eq1) : userInput.eq1;
  const eq2Str = isBusiness ? normalizeBusinessVars(userInput.eq2) : userInput.eq2;

  const E1 = parseEquationToStandard(eq1Str);
  const E2 = parseEquationToStandard(eq2Str);

  // oczekiwane równania (układ)
  const expectedSum = { ax: 1, ay: 1, c: -totalInitial };
  const expectedRel = isBusiness
    ? { ax: p1 / 100, ay: p2 / 100, c: -usedTotal }
    : { ax: kRatio * a1, ay: -a2, c: 0 };

  // równanie z jedną niewiadomą x (po podstawieniu y = total - x)
  // business: p1*x + p2*(T-x) = used
  // default : a2*(T-x) = k*a1*x
  const singleExpected = isBusiness
    ? {
        ax: (p1 - p2) / 100,
        ay: 0,
        c: (p2 / 100) * totalInitial - usedTotal,
      }
    : {
        ax: -(a2 + kRatio * a1),
        ay: 0,
        c: a2 * totalInitial,
      };

  let systemOK = false;

  if (E1 && E2) {
    const option1 =
      equationsEquivalent(E1, expectedSum) &&
      equationsEquivalent(E2, expectedRel);
    const option2 =
      equationsEquivalent(E1, expectedRel) &&
      equationsEquivalent(E2, expectedSum);
    systemOK = option1 || option2;
  } else {
    const single = E1 || E2;
    const singleRaw = E1 ? eq1Str : eq2Str;
    if (
      single &&
      !isBareSolvedEquation(singleRaw) &&
      equationsEquivalent(single, singleExpected)
    ) {
      systemOK = true;
    }
  }

  const ux = parseNumberFromAny(userInput.x);

  const solutionOK = Number.isFinite(ux) && eq(ux, xCorrect);

  // Polityka oceniania:
  // - poprawne rozwiązanie końcowe x daje pełną punktację,
  //   nawet jeśli zapis układu/równania ma błąd formalny.
  // - gdy x jest błędne, punkt za układ/równanie działa standardowo.
  const score = solutionOK ? maxPoints : (systemOK ? 1 : 0);

  const parts = {
    "równanie / układ równań opisujący zadanie": systemOK,
    "rozwiązanie zadania (x)": solutionOK,
  };

  return {
    isCorrect: score === maxPoints,
    score,
    maxScore: maxPoints,
    parts,
  };
}

/* ============================================================================
 * WYJAŚNIENIE KROK PO KROKU
 * ========================================================================== */

export function renderExplanation({ data }) {
  const { scenario, totalInitial, p1, p2, r, xCorrect, yCorrect, usedTotal } = data;

  const a1 = (100 - p1) / 100;
  const a2 = (100 - p2) / 100;
  const kRatio = r / 100;
  const isBusiness =
    scenario?.id === "marketing" || scenario?.id === "researchBudget";

  const totalLatex = integerLatex(totalInitial);
  const usedLatex = integerLatex(usedTotal);
  const p1UsedLatex = decimalLatex(p1 / 100, 2);
  const p2UsedLatex = decimalLatex(p2 / 100, 2);
  const a1Latex = decimalLatex(a1, 2);
  const a2Latex = decimalLatex(a2, 2);
  const kLatex = decimalLatex(kRatio, 2);

  const defaultSystemLatex = String.raw`\begin{cases}
x + y = ${totalLatex} \\
${a2Latex}y = ${kLatex}\cdot ${a1Latex}x
\end{cases}`;
  const businessSystemLatex = String.raw`\begin{cases}
x + y = ${totalLatex} \\
${p1UsedLatex}x + ${p2UsedLatex}y = ${usedLatex}
\end{cases}`;
  const systemLatex = isBusiness ? businessSystemLatex : defaultSystemLatex;

  const defaultSingleLatex = String.raw`${a2Latex}\cdot(${totalLatex} - x) = ${kLatex}\cdot ${a1Latex}x`;
  const businessSingleLatex = String.raw`\begin{aligned}
&${p1UsedLatex}x + ${p2UsedLatex}\cdot(${totalLatex} - x) \\
&\qquad = ${usedLatex}
\end{aligned}`;
  const singleLatex = isBusiness ? businessSingleLatex : defaultSingleLatex;

  const solLatex = String.raw`\begin{aligned}x &= ${integerLatex(xCorrect)} \\ y &= ${integerLatex(yCorrect)}\end{aligned}`;
  const xDesc = businessText(isBusiness
    ? `kwota przydzielona ${scenario.item1}`
    : `liczba ${scenario.item1}`, isBusiness);
  const yDesc = businessText(isBusiness
    ? `kwota przydzielona ${scenario.item2}`
    : `liczba ${scenario.item2}`, isBusiness);

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Oznaczamy zmienne:
      </p>
      <ul style={{ paddingLeft: "1.2rem", marginTop: 0 }}>
        <li>
          <InlineMath math={"x"} /> – {xDesc},
        </li>
        <li>
          <InlineMath math={"y"} /> – {yDesc}.
        </li>
      </ul>

      <p>Na podstawie treści zadania zapisujemy układ równań:</p>
      <BlockMath math={systemLatex} />

      <p>
        Pierwsze równanie opisuje {isBusiness ? "łączną przydzieloną kwotę" : `łączną liczbę ${scenario.objectTotal}`}, a drugie{" "}
        {isBusiness
          ? "opisuje łączną kwotę wykorzystaną po uwzględnieniu podanych procentów."
          : "opisuje zależność między liczbą elementów, które pozostały po odjęciu odpowiednich procentów w obu grupach."}
      </p>

      <p>
        Możemy teraz z pierwszego równania wyznaczyć{" "}
        <InlineMath math={"y"} />:
      </p>
      <BlockMath math={`y = ${totalLatex} - x`} />

      <p>
        i po podstawieniu tej wartości za{" "}
        <InlineMath math={"y"} /> do drugiego równania otrzymujemy jedno
        równanie z niewiadomą <InlineMath math={"x"} />:
      </p>
      <BlockMath math={singleLatex} />

      <p>Po rozwiązaniu tego równania otrzymujemy:</p>
      <BlockMath math={solLatex} />

      <p>
        <strong>Krok 2.</strong> Odpowiadamy na pytanie z zadania.
      </p>
      <p>
        Zatem {isBusiness ? "kwota przydzielona" : "liczba"} {businessText(scenario.item1, isBusiness)} wynosi{" "}
        <InlineMath math={`x = ${integerLatex(xCorrect)}`} />{" "}
        {scenario.answerNoun}.
      </p>
    </div>
  );
}
